<template>
  <span>
    <table-button v-bind="$props" name="init" @click="fetch" :is-loading="isFetching" v-if="!isFetched"> </table-button>
    <dots-menu
      attach
      v-model="isMenuOpened"
      v-else
      exact-items
      :items="intersectingLines"
      close-on-content-click
      @selectedItem="onJoinLineSelected"
      @mouseleave="onHover($event, false)"
      @mouseenter="onHover($event, true)"
    >
      <template #activator="{ on }">
        <v-tooltip bottom color="font">
          <template #activator="{ on: onTooltip }">
            <v-btn icon v-on="{ ...on, ...onTooltip }" :disabled="disabled" v-bind="$props">
              <v-icon :size="fontSize" color="rgba(0,0,0,0.7)">
                {{ icon }}
              </v-icon>
            </v-btn>
          </template>
          {{ tooltip }}
        </v-tooltip>
      </template>
    </dots-menu>
  </span>
</template>

<script>
import { call, get } from 'vuex-pathify';

import DotsMenu from '@/components/DotsMenu';
import TableButton from '@/components/TableButton';

export default {
  name: 'TableButtonsEditJoinLines',
  components: {
    DotsMenu,
    TableButton,
  },
  props: {
    datasource: {
      type: String,
      required: true,
    },
    fontSize: {
      type: String,
      default: '24px',
    },
    icon: {
      type: String,
      default: '',
    },
    tooltip: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    selectedFeatureId: {
      type: Number,
      default: null,
    },
    layerId: {
      type: [Number, String],
      required: true,
    },
    topological: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    layerMetadata: get('layers/metadata'),
    dataSourceGeometryName() {
      return this.layerMetadata[this.datasource].attributes_schema.geometry_name;
    },
  },
  data: () => ({
    isFetching: false,
    isFetched: false,
    isMenuOpened: false,
    intersectingLines: [],
  }),
  watch: {
    selectedFeatureId: 'resetFetchedItems',
  },
  methods: {
    getDataSourceFeature: call('layers/getDataSourceFeature'),
    getIntersectingLines: call('layers/getIntersectingLines'),
    getLayerFeatures: call('layers/getLayerFeatures'),
    onHover(item, value) {
      const { layerId } = this;
      const { id } = item.params;
      if (value) {
        this.$store.set('map/ADD_SELECTED_FEATURES!', {
          type: 'highlitedFeatures',
          features: {
            [layerId]: [id],
          },
        });
      } else {
        this.$store.set('map/DELETE_SELECTED_FEATURES!', {
          type: 'highlitedFeatures',
          features: {
            [layerId]: [id],
          },
        });
      }
    },
    getIntersectingLinesArray(lines) {
      return lines
        .filter(line => line !== this.selectedFeatureId)
        .map(line => ({
          name: `ID ${line}`,
          params: {
            id: line,
          },
        }));
    },
    async fetch() {
      try {
        this.isFetching = true;
        const { selectedFeatureId, datasource, dataSourceGeometryName, layerId } = this;
        if (this.topological) {
          const r = await this.getIntersectingLines({ id: selectedFeatureId, datasource });
          this.intersectingLines = this.getIntersectingLinesArray(r.data.data);
        } else {
          const r = await this.getDataSourceFeature({ dataSource: datasource, feature_id: selectedFeatureId });
          const geometry = r.geometry;
          const multiPointCoords = [];
          if (geometry.type === 'LineString') {
            multiPointCoords.push(geometry.coordinates[0]);
            multiPointCoords.push(geometry.coordinates.slice(-1)[0]);
          } else if (geometry.type === 'MultiLineString') {
            geometry.coordinates.forEach(line => {
              multiPointCoords.push(line[0]);
              multiPointCoords.push(line.slice(-1)[0]);
            });
          }
          const filterGeometry = {
            coordinates: multiPointCoords,
            crs: {
              properties: {
                name: this.$_config.defaultEpsg,
              },
              type: 'name',
            },
            type: 'MultiPoint',
          };
          const data = {
            layer_id: layerId,
            features_filter: {
              '%SPATIALLY_INTERSECTS': {
                [`${datasource}.${dataSourceGeometryName}`]: [filterGeometry],
              },
            },
            params: { ids_only: true },
          };
          const rr = await this.getLayerFeatures(data);
          this.intersectingLines = this.getIntersectingLinesArray(rr.data.data.ids);
          if (this.intersectingLines.length < 1) {
            this.$store.set('snackbar/PUSH_MESSAGE!', {
              message: this.$i18n.t('sidebar.noObjectsToJoin'),
            });
            this.isFetching = false;
            return;
          }
        }
        this.isFetching = false;
        this.isFetched = true;
        await this.$nextTick();
        this.isMenuOpened = true;
      } catch (error) {
        console.log('  error', error);
        this.isFetching = false;
      }
    },
    onJoinLineSelected(itemName, item) {
      this.$emit('joinLines', item.params.id);
    },
    resetFetchedItems() {
      this.isFetched = false;
      this.intersectingLines = [];
    },
  },
};
</script>

<style lang="scss" scoped></style>
